import { CircleAlert, CircleCheck, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { LAYER } from './layers';
import { ToastContext, type ToastApi, type ToastMessage, type ToastTone } from './toastContext';

/** How long a SUCCESS stays. A failure has no timer at all — see `<ToastItem>`. */
const SUCCESS_MS = 5000;

/**
 * How many are drawn at once.
 *
 * A reader working down a list can raise one per row, and an unbounded stack walks off the
 * top of the window — so the OLDEST go, which is the right end to drop from: the newest is
 * the one they are waiting on. Four is what fits above the fold at this size.
 */
const MAX_VISIBLE = 4;

const TONES: Record<ToastTone, { ring: string; disc: string; glyph: string; icon: ReactNode }> = {
  success: {
    ring: 'border-emerald-200',
    disc: 'bg-emerald-100',
    glyph: 'text-emerald-700',
    icon: <CircleCheck className="h-4 w-4" aria-hidden="true" />,
  },
  failure: {
    ring: 'border-red-200',
    disc: 'bg-red-100',
    glyph: 'text-red-700',
    icon: <CircleAlert className="h-4 w-4" aria-hidden="true" />,
  },
};

/**
 * One toast.
 *
 * **A SUCCESS dismisses itself and a FAILURE does not, and that asymmetry is the component.**
 * A confirmation the reader missed costs them nothing — the record on screen already shows
 * what happened. A refusal they missed is a write they believe went through, so it stays
 * until they close it. That is the inline error strip's own rule — *an error is not an aside,
 * it must be impossible to miss* — kept rather than traded away for a tidier animation.
 *
 * **THE TIMER PAUSES ON HOVER AND ON FOCUS.** A notice that vanishes while the reader is
 * reading it, or while they are reaching for its close button, is the classic failure of this
 * control — and focus is not optional beside hover, for the reason every other reveal in this
 * product pairs the two: a keyboard reader generates no hover at all.
 */
function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const tone = TONES[toast.tone];

  /*
    `onDismiss` is called from a timeout, so it is held in a ref rather than named as a
    dependency: listed, a parent re-render would restart the countdown from zero and a toast
    on a busy page would never reach its own deadline.
  */
  const dismiss = useRef(onDismiss);

  /* Written in an EFFECT, never during render — a ref mutated mid-render is a write React is
     free to discard on a re-render it threw away, and in StrictMode that is every first one. */
  useEffect(() => {
    dismiss.current = onDismiss;
  });

  useEffect(() => {
    if (toast.tone !== 'success' || paused) return;

    const timer = window.setTimeout(() => dismiss.current(), SUCCESS_MS);

    return () => window.clearTimeout(timer);
  }, [toast.tone, paused]);

  return (
    <div
      /*
        THE ROLE IS ON THE ITEM rather than on a live region around the stack. A failure is
        `alert`, which interrupts; a success is `status`, which waits for a pause. One region
        around both could only carry one politeness, and nesting an `alert` inside a `polite`
        region is ambiguous in a way no two screen readers agree about.
      */
      role={toast.tone === 'failure' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`toast-enter pointer-events-auto flex w-[min(28rem,calc(100vw-2rem))] items-start gap-3 rounded-panel border bg-white px-4 py-3 shadow-raised ${tone.ring}`}
    >
      {/*
        THE GLYPH SITS IN A DISC, the treatment `<NextActionAlert>` already uses so the two
        surfaces that announce an outcome read as one product. **The STEP is not the same and
        cannot be**: that banner stands on a `-50` ground and takes a `-100` disc, one step
        deeper; this card is WHITE, and a `-50` disc on white is a shape nobody can see. So
        the disc keeps `-100` and the card gives up the step rather than the disc giving up
        its legibility.

        `shrink-0`, or a long message squeezes the disc into an oval.
      */}
      <span
        className={`mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone.disc} ${tone.glyph}`}
      >
        {tone.icon}
      </span>

      {/*
        THE GROUND IS WHITE and the TONE is carried by the border and the disc. A tinted card
        would read fine over a white panel and poorly over the server error it is most likely
        to be quoting; slate-700 on white is the one pairing that holds whatever the message
        turns out to say.
      */}
      <p className="min-w-0 flex-1 break-words pt-1 text-xs text-slate-700">{toast.message}</p>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-1 mt-0.5 shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * The product's toast stack — reference §2's `Toast.tsx`.
 *
 * **It is a PROVIDER above the routes, not page state**, which is the upload dock's own
 * placement and for the same reason one layer along: the outcome of a write outlives the
 * surface that made it. A modal closes on save and a form may navigate, so a toast owned by
 * either would be unmounted by the thing it exists to report on.
 *
 * **BOTTOM-CENTRE.** The dock is `bottom-4 right-4` and the nav rail owns the left edge, so
 * the centre is the one bottom anchor that collides with neither — and it is where a reader
 * already expects this control.
 *
 * **It renders an empty fixed box when nothing is queued**, which costs no markup a reader can
 * meet and is `pointer-events-none`, so the page underneath is never blocked by a notice that
 * is not there.
 *
 * **Motion is the stylesheet's.** `index.css` already kills every animation under
 * `prefers-reduced-motion`, so a second guard here would be a rule written twice.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(0);

  /*
    THE STACK IS THE SESSION'S, and it is emptied when the session ends.

    A failure never dismisses itself, and `clearSession` is a state change rather than a
    reload — so a refusal raised moments before sign-out stayed on screen, over the login
    form, naming the record it was about, until somebody closed it. On a shared machine that
    is the previous reader's last error shown to the next one. VERIFIED before the fix: signed
    out with one up, and it was still there on `/login`.

    It is the cost of the provider sitting above the routes, which is the right placement for
    every other reason — so the stack is cleared rather than the placement given up.

    ADJUSTED DURING RENDER rather than in an effect, which is React's own documented pattern
    for state that has to follow an input: an effect would paint the stale stack once over the
    login form before clearing it, and that frame is exactly the thing being prevented.
  */
  const [sessionUser, setSessionUser] = useState(user);

  if (sessionUser !== user) {
    setSessionUser(user);
    if (!user) setToasts([]);
  }

  const push = useCallback((tone: ToastTone, message: string) => {
    /*
      THE ID IS MINTED HERE, not inside the updater. An updater runs during RENDER and
      StrictMode deliberately invokes it twice, so a counter incremented in there advances
      twice per toast — harmless for uniqueness and wrong about everything else, which is the
      shape of defect that survives review. `push` is only ever called from an event handler
      or an awaited continuation, where mutating a ref is exactly what refs are for.
    */
    nextId.current += 1;
    const id = nextId.current;

    /* NEWEST LAST, so it lands nearest the bottom edge and the stack grows upward. */
    setToasts((current) => [...current, { id, tone, message }].slice(-MAX_VISIBLE));
  }, []);

  /*
    THE API IS STABLE, so a caller may name it in an effect's dependencies without the effect
    re-running on every render of the tree below this provider.
  */
  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      failure: (message) => push('failure', message),
    }),
    [push],
  );

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}

      <div
        className={`pointer-events-none fixed inset-x-0 bottom-4 flex flex-col items-center gap-2 px-4 ${LAYER.TOAST}`}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
